import React, { useState, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import { RightPanel } from './RightPanel';

const PANEL_WIDTH_KEY = 'wms-panel-width';
const MIN_WIDTH = 280;
const MAX_WIDTH_RATIO = 0.5;
const DEFAULT_WIDTH = 350;

interface GraphEditorLayoutProps {
  children: React.ReactNode;
}

export function GraphEditorLayout({ children }: GraphEditorLayoutProps) {
  const [panelWidth, setPanelWidth] = useState(() => {
    try {
      const saved = localStorage.getItem(PANEL_WIDTH_KEY);
      if (saved) {
        const parsed = parseInt(saved, 10);
        if (!Number.isNaN(parsed) && parsed >= MIN_WIDTH) return parsed;
      }
    } catch {
      // localStorage unavailable
    }
    return DEFAULT_WIDTH;
  });

  const widthRef = useRef(panelWidth);
  widthRef.current = panelWidth;

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = widthRef.current;

      const onMouseMove = (ev: MouseEvent) => {
        const delta = startX - ev.clientX;
        const maxWidth = Math.floor(window.innerWidth * MAX_WIDTH_RATIO);
        const newWidth = Math.min(Math.max(startWidth + delta, MIN_WIDTH), maxWidth);
        setPanelWidth(newWidth);
        widthRef.current = newWidth;
      };

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        try {
          localStorage.setItem(PANEL_WIDTH_KEY, String(widthRef.current));
        } catch {
          // localStorage unavailable
        }
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [],
  );

  return (
    <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Canvas takes remaining space */}
      <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>{children}</Box>

      {/* Drag handle */}
      <Box
        onMouseDown={onMouseDown}
        sx={{
          width: 6,
          cursor: 'col-resize',
          bgcolor: '#e5e7eb',
          '&:hover': { bgcolor: '#2563eb' },
          transition: 'background-color 0.15s',
          flexShrink: 0,
        }}
      />

      {/* Right panel */}
      <Box
        sx={{
          width: panelWidth,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <RightPanel />
      </Box>
    </Box>
  );
}
